'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import GXGLayout from '@/components/GXGLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const CoinIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b"><circle cx="12" cy="12" r="10"/></svg>;
const TargetIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
const FireIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>;
const UploadIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>;

interface Mission {
  id: string;
  title: string;
  description: string;
  difficulty: string;
  coin_reward?: number;
  reward_coins?: number;
  game_label?: string;
  status?: string;
  proof_required?: boolean;
  proof_type?: string;
  proof_count_required?: number;
  proof_instructions?: string;
  approval_required?: boolean;
  animated_banner_url?: string;
  animated_background_url?: string;
  mission_video_url?: string;
  mission_gif_url?: string;
  card_image_url?: string;
}

interface Submission {
  mission_id: string;
  status: string;
}

const diffColor = (d: string) => {
  if (d === 'legendary') return 'badge-gold';
  if (d === 'hard') return 'badge-red';
  if (d === 'medium') return 'badge-blue';
  return 'badge-green';
};

const diffGlow = (d: string) => {
  if (d === 'legendary') return 'shadow-[0_0_18px_rgba(245,158,11,0.35)]';
  if (d === 'hard') return 'shadow-[0_0_18px_rgba(239,68,68,0.3)]';
  if (d === 'medium') return 'shadow-[0_0_18px_rgba(59,130,246,0.3)]';
  return 'shadow-[0_0_18px_rgba(16,185,129,0.25)]';
};

const staticLeaderboard = [
  { rank: 1, name: 'NeoKai', missions: 156, coins: 24680 },
  { rank: 2, name: 'PixelRonin', missions: 89, coins: 12450 },
  { rank: 3, name: '8BitSamurai', missions: 74, coins: 11870 },
  { rank: 4, name: 'RetroRyu', missions: 61, coins: 9540 },
  { rank: 5, name: 'ArcadeMaven', missions: 55, coins: 8320 },
];

function MissionCard({ mission, submission, onTrack, tracked }: {
  mission: Mission;
  submission?: Submission;
  onTrack: (id: string) => void;
  tracked: boolean;
}) {
  const coins = mission.reward_coins ?? mission.coin_reward ?? 0;
  const hasMedia = mission.mission_gif_url || mission.mission_video_url || mission.card_image_url || mission.animated_banner_url;
  const status = submission?.status;

  const statusBadge = () => {
    if (status === 'pending_review') return <span className="badge-gold text-[9px]">⏳ PENDING REVIEW</span>;
    if (status === 'approved') return <span className="badge-green text-[9px]">✓ COMPLETED</span>;
    if (status === 'rejected') return <span className="badge-red text-[9px]">✗ REJECTED</span>;
    if (status === 'needs_more_proof') return <span className="badge-blue text-[9px]">📋 MORE PROOF NEEDED</span>;
    return null;
  };

  return (
    <div className={`mission-card relative overflow-hidden group transition-all duration-300 hover:scale-[1.02] ${diffGlow(mission.difficulty)}`}>
      {hasMedia && (
        <div className="absolute inset-0 z-0">
          {mission.mission_gif_url && (
            <img src={mission.mission_gif_url} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
          )}
          {!mission.mission_gif_url && mission.card_image_url && (
            <img src={mission.card_image_url} alt="" className="w-full h-full object-cover opacity-20" loading="lazy" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a1a] via-[#0a0a1a]/80 to-transparent" />
        </div>
      )}

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-2">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-800 to-blue-800 flex items-center justify-center flex-shrink-0">
            <TargetIcon />
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={diffColor(mission.difficulty)}>{mission.difficulty}</span>
            {statusBadge()}
          </div>
        </div>

        <h3 className="font-semibold text-sm text-white mb-1">{mission.title}</h3>
        <p className="text-xs text-white/45 mb-2">{mission.description}</p>

        {mission.game_label && <div className="text-[11px] text-white/40 mb-2">{mission.game_label}</div>}

        {mission.proof_required && (
          <div className="flex items-center gap-1 mb-3">
            <span className="flex items-center gap-1 text-[10px] text-gxg-purple2 bg-purple-500/15 border border-purple-500/25 rounded px-1.5 py-0.5">
              <UploadIcon /> Proof Required
              {(mission.proof_count_required ?? 1) > 1 && ` (${mission.proof_count_required}x)`}
            </span>
            {mission.approval_required && (
              <span className="text-[10px] text-white/35 bg-white/5 rounded px-1.5 py-0.5">Admin Review</span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-gxg-gold text-xs font-mono font-semibold">
            <CoinIcon /> {coins}
          </div>
          {status === 'pending_review' || status === 'approved' ? (
            <Link href={`/missions/${mission.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-purple-500/20 text-gxg-purple2 border border-purple-500/30">
              View Status
            </Link>
          ) : status === 'needs_more_proof' ? (
            <Link href={`/missions/${mission.id}`} className="text-xs px-3 py-1.5 rounded-lg btn-gxg-pink">
              Add More Proof
            </Link>
          ) : (
            <Link href={`/missions/${mission.id}`} className="text-xs px-3 py-1.5 rounded-lg btn-gxg-primary">
              {tracked ? 'View Mission' : mission.proof_required ? 'Start Mission' : 'View Mission'}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MissionsPage() {
  const { user } = useAuth();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [tracked, setTracked] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = createClient();
      const { data: missionData } = await supabase
        .from('missions')
        .select('*')
        .eq('status', 'Active')
        .order('created_at', { ascending: false });

      setMissions(missionData || []);

      if (user?.id) {
        const { data: subData } = await supabase
          .from('mission_submissions')
          .select('mission_id, status')
          .eq('user_id', user.id);
        if (subData) setSubmissions(subData);
      }
    } catch (err) {
      console.error('Load missions error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getSubmission = (missionId: string) =>
    submissions.find(s => s.mission_id === missionId);

  return (
    <GXGLayout>
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-display font-black text-3xl md:text-4xl text-white mb-2">
            Take on Missions. <span className="gxg-pink-text">Join the Community.</span>
          </h1>
          <p className="text-white/50 text-sm">Complete quests, upload proof, earn coins, and climb the leaderboard.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Active Missions', value: loading ? '...' : String(missions.length), color: 'text-gxg-purple2', icon: <TargetIcon /> },
            { label: 'Events This Week', value: '3', color: 'text-gxg-pink2', icon: <FireIcon /> },
            { label: 'GXG Members', value: '12,458', color: 'text-gxg-gold', icon: <CoinIcon /> },
          ].map((stat, i) => (
            <div key={i} className="stat-card text-center">
              <div className="flex justify-center mb-2">{stat.icon}</div>
              <div className={`font-display font-bold text-2xl ${stat.color}`}>{stat.value}</div>
              <div className="text-xs text-white/40 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Featured Event */}
        <div className="glass-card p-5 neon-border-pink mb-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-900/15 to-purple-900/10 pointer-events-none" />
          <div className="relative z-10 flex flex-col md:flex-row gap-4">
            <div className="w-full md:w-48 h-32 rounded-lg bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center flex-shrink-0">
              <span className="font-display font-black text-2xl text-gxg-pink opacity-80">GXG</span>
            </div>
            <div className="flex-1">
              <div className="badge-gold text-[10px] mb-2 inline-block">⭐ TONIGHT&apos;S FEATURED QUEST</div>
              <h2 className="font-display font-bold text-xl text-white mb-1">Retro Night at GXG Lounge</h2>
              <p className="text-white/50 text-sm mb-3">Join the community, play classics, and earn 2X coins all night long!</p>
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="badge-purple">MULTI-GAME</span>
                <span className="badge-blue">SOCIAL</span>
                <span className="badge-gold">2X COINS</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-white/50">
                <span>📍 GXG Lounge</span>
                <span>🕐 7:00 PM – 11:00 PM EST</span>
              </div>
            </div>
            <div className="flex-shrink-0">
              <button className="btn-gxg-pink px-5 py-2.5 rounded-lg text-sm">Join Quest →</button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Mission Board */}
          <div className="xl:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg text-white">Mission Board</h2>
              {loading && <svg className="animate-spin w-4 h-4 text-gxg-purple2" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>}
            </div>

            {!loading && missions.length === 0 ? (
              <div className="glass-card p-10 text-center text-white/30">
                <div className="text-4xl mb-3">🎯</div>
                <div className="text-sm">No active missions right now. Check back soon!</div>
                <div className="text-xs mt-2 text-white/20">Missions are created and managed by admins.</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {missions.map(mission => (
                  <MissionCard
                    key={mission.id}
                    mission={mission}
                    submission={getSubmission(mission.id)}
                    onTrack={(id) => setTracked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                    tracked={tracked.includes(mission.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right: Leaderboard + Rewards */}
          <div className="space-y-5">
            {/* Leaderboard */}
            <div className="glass-card p-5">
              <h2 className="font-display font-bold text-base text-white mb-4">Top Players</h2>
              <div className="space-y-2">
                {staticLeaderboard.map(player => (
                  <div key={player.rank} className="leaderboard-row">
                    <span className={`w-6 text-center text-sm font-bold font-mono ${player.rank === 1 ? 'text-gxg-gold' : 'text-white/40'}`}>{player.rank}</span>
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                      {player.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-white/90">{player.name}</div>
                      <div className="text-[10px] text-white/35">{player.missions} missions</div>
                    </div>
                    <div className="flex items-center gap-1 text-xs font-mono text-gxg-gold">
                      {player.coins.toLocaleString()} <CoinIcon />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Rewards Teaser */}
            <div className="glass-card p-5 neon-border-gold">
              <div className="badge-gold text-[10px] mb-3 inline-block">🏆 REWARDS TEASER</div>
              <h3 className="font-display font-bold text-base text-white mb-3">Complete Missions, Earn Rewards</h3>
              <div className="space-y-2">
                {[
                  { name: 'Legendary Cartridge', coins: '5,000', rarity: 'EXCLUSIVE' },
                  { name: 'Speed Runner Badge', coins: '300', rarity: 'COMMON' },
                  { name: 'Retro Night Ticket', coins: '1,000', rarity: 'RARE' },
                ].map((reward, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white/3">
                    <div className="text-xs font-semibold text-white/80">{reward.name}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-gxg-gold">{reward.coins}</span>
                      <span className="badge-gold text-[9px]">{reward.rarity}</span>
                    </div>
                  </div>
                ))}
              </div>
              <Link href="/rewards" className="btn-gxg-outline text-xs w-full mt-3 py-2 rounded-lg block text-center">View All Rewards</Link>
            </div>

            {/* My Submissions */}
            {user && submissions.length > 0 && (
              <div className="glass-card p-5">
                <h3 className="font-display font-bold text-sm text-white mb-3">My Submissions</h3>
                <div className="space-y-2">
                  {submissions.slice(0, 4).map((sub, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-white/60 truncate flex-1 mr-2">{sub.mission_id.slice(0, 8)}...</span>
                      <span className={
                        sub.status === 'approved' ? 'badge-green text-[9px]' :
                        sub.status === 'rejected' ? 'badge-red text-[9px]' :
                        sub.status === 'needs_more_proof' ? 'badge-blue text-[9px]' :
                        'badge-gold text-[9px]'
                      }>{sub.status.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
                <Link href="/passport" className="text-gxg-purple2 text-xs mt-3 block hover:text-gxg-pink transition-colors">View All in Passport →</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </GXGLayout>
  );
}
